import type { MutationFunctionOptions } from '@apollo/client';
import type {
  ApproveCampaignMutation,
  ArchiveCampaignMutation,
  Campaign,
  CancelApproveCampaignMutation,
  CloneCampaignMutation,
  CreateCampaignInput,
  CreateCampaignMutation,
  Exact,
  Placement,
  PlacementInput,
  PublishCampaignMutation,
  RequestApproveCampaignMutation,
  RestoreCampaignMutation,
  SaveCampaignInput,
  SaveCampaignMutation,
  SuspendCampaignMutation,
} from 'generated/graphql';

const getPlacementInput = (campaign: Campaign): PlacementInput[] =>
  campaign.placements.map((placement: Placement) => ({
    additionalExcludes: placement.additionalExcludes,
    additionalIncludes: placement.additionalIncludes,
    asins: placement.asins,
    browseNodes: placement.browseNodes,
    bullseyeExcludes: placement.bullseyeExcludes,
    bullseyeIncludes: placement.bullseyeIncludes,
    categoryNode: placement.categoryNode,
    customerTargeting: placement.customerTargeting,
    deviceTypes: placement.deviceTypes,
    event: placement.event
      ? {
          childEventId: placement.event.childEventId,
          elemId: placement.event.elemId,
        }
      : undefined,
    guarantee: parseInt((placement.guarantee as string | undefined) ?? '', 10),
    impressionsDaysTimeout: parseInt((placement.impressionsDaysTimeout as string | undefined) ?? '', 10),
    impressionsLimit: parseInt((placement.impressionsLimit as string | undefined) ?? '', 10),
    kothOrGuarantee: placement.kothOrGuarantee,
    languages: placement.languages,
    mvtAssignmentId: placement.mvtAssignmentId,
    mvtName: placement.mvtName,
    pageId: placement.pageId,
    pageType: placement.pageType,
    platform: placement.platform,
    scheduleType: placement.scheduleType,
    slot: placement.slot,
    timeRange: {
      end: placement.timeRange.end,
      start: placement.timeRange.start,
    },
    uiId: placement.uiId,
    weblabName: placement.weblabName,
    weblabTreatment: placement.weblabTreatment,
  }));

export const getCreateCampaignInput = (
  campaign: Campaign,
  campaignTypeVersion: string,
): MutationFunctionOptions<
  CreateCampaignMutation,
  Exact<{
    input: CreateCampaignInput;
  }>
> => ({
  variables: {
    input: {
      accessControlGroup: campaign.accessControlGroup,
      additionalName: campaign.additionalName,
      alexaEnabled: campaign.alexaEnabled,
      businessGroup: campaign.businessGroup,
      campaignTypeVersion,
      content: campaign.content,
      eventShortName: campaign.eventShortName,
      id: campaign.id,
      intent: campaign.intent,
      isAutoGenerateCreativeName: campaign.isAutoGenerateCreativeName,
      isCampaignValid: true,
      isMVT: campaign.isMVT,
      language: campaign.language,
      mvtExperimentInfo: campaign.mvtExperimentInfo,
      placements: getPlacementInput(campaign),
      productLine: campaign.productLine,
      program: campaign.program,
      projectId: campaign.projectId,
      publishConfig: {},
      reason: campaign.reason,
      tags: campaign.tags,
      title: campaign.title,
      typeId: campaign.typeId,
    },
  },
});

export const getSaveCampaignInput = (
  campaign: Campaign,
  campaignTypeVersion: string,
): MutationFunctionOptions<
  SaveCampaignMutation,
  Exact<{
    input: SaveCampaignInput;
  }>
> => ({
  variables: {
    input: {
      accessControlGroup: campaign.accessControlGroup,
      additionalName: campaign.additionalName,
      alexaEnabled: campaign.alexaEnabled,
      businessGroup: campaign.businessGroup,
      campaignTypeVersion,
      content: campaign.content,
      eventShortName: campaign.eventShortName,
      id: campaign.id,
      intent: campaign.intent,
      isAutoGenerateCreativeName: campaign.isAutoGenerateCreativeName,
      isCampaignValid: true,
      isPropertyOrderSignificantInSymphonyContent: true,
      language: campaign.language,
      mvtExperimentInfo: campaign.mvtExperimentInfo,
      placements: getPlacementInput(campaign),
      productLine: campaign.productLine,
      program: campaign.program,
      projectId: campaign.projectId,
      publishConfig: {},
      reason: campaign.reason,
      symphonyContent: {},
      tags: campaign.tags,
      title: campaign.title,
      userVersion: campaign.userVersion,
    },
  },
});

export const getPublishCampaignInput = (
  campaign: Campaign,
): MutationFunctionOptions<
  PublishCampaignMutation,
  Exact<{
    campaignId: string;
    projectId: string;
  }>
> => ({
  variables: {
    campaignId: campaign.id,
    projectId: campaign.projectId,
  },
});

export const getRequestApproveCampaignInput = (
  campaign: Campaign,
): MutationFunctionOptions<
  RequestApproveCampaignMutation,
  Exact<{
    campaignId: string;
    projectId: string;
  }>
> => ({
  variables: {
    campaignId: campaign.id,
    projectId: campaign.projectId,
  },
});

export const getCancelApproveCampaignInput = (
  campaign: Campaign,
): MutationFunctionOptions<
  CancelApproveCampaignMutation,
  Exact<{
    campaignId: string;
    projectId: string;
  }>
> => ({
  variables: {
    campaignId: campaign.id,
    projectId: campaign.projectId,
  },
});

export const getApproveCampaignInput = (
  campaign: Campaign,
): MutationFunctionOptions<
  ApproveCampaignMutation,
  Exact<{
    campaignId: string;
    projectId: string;
  }>
> => ({
  variables: {
    campaignId: campaign.id,
    projectId: campaign.projectId,
  },
});

export const getCloneCampaignInput = (
  campaign: Campaign,
  newCampaignId: string,
): MutationFunctionOptions<
  CloneCampaignMutation,
  Exact<{
    campaignId: string;
    projectId: string;
    newCampaignId: string;
  }>
> => ({
  variables: {
    campaignId: campaign.id,
    newCampaignId,
    projectId: campaign.projectId,
  },
});

export const getArchiveCampaignInput = (
  campaign: Campaign,
): MutationFunctionOptions<
  ArchiveCampaignMutation,
  Exact<{
    campaignId: string;
    projectId: string;
    userVersion: number;
  }>
> => ({
  variables: {
    campaignId: campaign.id,
    projectId: campaign.projectId,
    userVersion: campaign.userVersion,
  },
});

export const getSuspendCampaignInput = (
  campaign: Campaign,
): MutationFunctionOptions<
  SuspendCampaignMutation,
  Exact<{
    campaignId: string;
    projectId: string;
  }>
> => ({
  variables: {
    campaignId: campaign.id,
    projectId: campaign.projectId,
  },
});

export const getRestoreCampaignInput = (
  campaign: Campaign,
): MutationFunctionOptions<
  RestoreCampaignMutation,
  Exact<{
    campaignId: string;
    projectId: string;
    userVersion: number;
  }>
> => ({
  variables: {
    campaignId: campaign.id,
    projectId: campaign.projectId,
    userVersion: campaign.userVersion,
  },
});
